import configJson from '../config/config.json';
import { multiTypePoolManager } from '../constants/vpool';
import { handleResponse } from './apiService';
import { assetAmountInWei } from './assetsService';
import { getChampionToken } from './championService';
import { newContractReader, useContractReader } from './contractReader';
import { getPercentage, PARTICIPATION_FEE } from './utilsService';
import { ethToWei } from './web3Service';

// API CALLS
export const getWhitelisted = (body) => {
  const config = {
    method: 'POST',
    headers: { Authorization: 'Bearer GUEST' },
    body: JSON.stringify(body),
  };
  const url = new URL(`${configJson.apiPath}/whitelist`);
  return fetch(url, config).then(handleResponse);
};

export const getWhitelistedPoolsForUser = (account) => {
  const config = {
    method: 'GET',
    headers: { Authorization: 'Bearer GUEST' },
  };
  const urlParams = new URLSearchParams({ wallet_address: account }).toString();
  const url = new URL(`${configJson.apiPath}/whitelist?${urlParams}`);
  return fetch(url, config).then(handleResponse);
};

export const getWhitelisteSignature = (userWallet, vpoolId) => {
  const config = {
    method: 'GET',
    headers: { Authorization: 'Bearer GUEST' },
  };
  const urlParams = new URLSearchParams({ wallet_address: userWallet, vpool_id: vpoolId }).toString();
  const url = new URL(`${configJson.apiPath}/whitelist/signature?${urlParams}`);
  return fetch(url, config).then(handleResponse);
};

export const sendManualSwaps = (body) => {
  const config = {
    method: 'POST',
    headers: { Authorization: `Bearer ${getChampionToken()}` },
    body: JSON.stringify(body),
  };
  const url = new URL(`${configJson.apiPath}/manual_swap`);
  return fetch(url, config).then(handleResponse);
};

// SMART CONTRACTS READS
export const participateInVpool = (vpoolId, signature, account, value, feePercentage) =>
  new Promise((res, rej) => {
    const amountToCharge = ethToWei(value);
    const feeInWei = ethToWei(feePercentage);
    if (signature?.length < 132) rej('Invalid Signature!');
    res(
      newContractReader({
        contractName: multiTypePoolManager(true).contractName,
        functionName: 'subscribeForVPool',
        params: [signature, vpoolId, amountToCharge, feeInWei],
        sendParams: { from: account },
      }),
    );
  });

export const getMinMaxParticipation = () => {
  const promises = [
    newContractReader({ contractName: 'HordVpoolConfiguration', functionName: 'minUserParticipation' }),
    newContractReader({ contractName: 'HordVpoolConfiguration', functionName: 'maxUserParticipation' }),
  ];

  return Promise.allSettled(promises);
};

export const getVpoolInfo = (web3HpoolId) =>
  newContractReader({
    contractName: 'HordVpoolManager',
    functionName: 'getPoolInfo',
    params: [web3HpoolId],
  });

export const getPoolsUserSubscribedFor = (walletAddress) =>
  newContractReader({
    contractName: 'HordVpoolManager',
    functionName: 'getPoolsUserSubscribedFor',
    params: [walletAddress],
  });

export const vpoolCheckChampionDepositRefund = (poolId) =>
  useContractReader('HordVpoolManager', 'isChampionDepositWithdrawn', [poolId]);

export const vpoolRefundChampionOnFail = (account, poolId) =>
  newContractReader({
    contractName: 'HordVpoolManager',
    functionName: 'withdrawDepositToChampion',
    params: [poolId],
    sendParams: { from: account },
  });

export const vpoolWithdrawDepositReq = (account, poolId) =>
  newContractReader({
    contractName: 'HordVpoolManager',
    functionName: 'withdrawDeposit',
    params: [poolId],
    sendParams: { from: account },
  });

export const vpoolGetUserSubscriptionForPool = async (poolId, account) => {
  const subscriptionInfo = await newContractReader({
    contractName: 'HordVpoolManager',
    functionName: 'getUserSubscriptionForPool',
    params: [poolId, account],
  });
  return subscriptionInfo;
};

export const approvedAmountOnOrder = (account, amount, tokenAddress, vpoolAddress) =>
  newContractReader({
    contractName: 'HordToken',
    functionName: 'approve',
    contractAddress: tokenAddress,
    params: [vpoolAddress, amount],
    sendParams: { from: account },
  });

export const claimVpoolTokens = (tokenAddress, account) =>
  newContractReader({
    contractName: 'VPool',
    functionName: 'claimVPoolTokens',
    contractAddress: tokenAddress,
    sendParams: { from: account },
  });

export const getMyVpoolTokens = (tokenAddress, account) =>
  newContractReader({
    contractName: 'VPool',
    functionName: 'getNumberOfTokensUserCanClaim',
    contractAddress: tokenAddress,
    params: [account],
  });

export const balanceOf = (tokenAddress, account) =>
  newContractReader({
    contractName: 'VPool',
    functionName: 'balanceOf',
    contractAddress: tokenAddress,
    params: [account],
  });

export const getMaxPoolAllocation = async () => {
  const maxPoolAllocation = await newContractReader({
    contractName: 'HordVpoolConfiguration',
    functionName: 'maxFollowerUSDStake',
  });

  return maxPoolAllocation;
};

export const isChampionWhitelisted = (champAddress) => {
  if (!champAddress) return false;
  return newContractReader({
    contractName: 'HordVpoolConfiguration',
    functionName: 'isHordChampion',
    params: [champAddress],
  });
};

export const calculateParticipationFeeAmount = (amount) => getPercentage(PARTICIPATION_FEE, +amount);
export const calculateParticipationAmount = (amount) => getPercentage(PARTICIPATION_FEE, +amount) + +amount;
